import React, {useCallback, useContext, useEffect, useRef, useState} from "react"
import RefreshTimer from "../../util/RefreshTimer";
import {LEDControllerOverviewContext} from "./context/LEDControllerOverviewContext";
import Row from "@meridian/components/row";
import {
    chainWalk,
    getAssetRefreshInterval, getCurrentDate
} from "../../util/Util";
import Column from "@amzn/meridian/column";
import {FormattedMessage, injectIntl} from "react-intl";
import {
    BLUE_COLOR, ONLINE_OK, LED_CONTROLLER_ASSET_TYPE,
    ORANGE_COLOR,
} from "../../util/Constants";
import plusCircleTokens from "@amzn/meridian-tokens/base/icon/plus-circle";
import updateTokens from "@amzn/meridian-tokens/base/icon/edit";
import Button from "@amzn/meridian/button";
import Table, {TableCell, TableRow} from "@amzn/meridian/table";
import Icon from "@amzn/meridian/icon";
import Box from "@amzn/meridian/box";
import Tooltip from "@amzn/meridian/tooltip";
import LoaderView from "../../loader/Loader";
import chevronDownLargeTokens from "@amzn/meridian-tokens/base/icon/chevron-down-large";
import Alert from "@amzn/meridian/alert";
import Text from "@amzn/meridian/text";
import Menu, {MenuItem} from "@amzn/meridian/menu";
import DisableMonitoringModal from "../../commonui/DisableMonitoringModal";
import {
    checkDeviceMonitoringEnabled, hasAllNonNullFields,
    getMonitoringStatusDetails
} from "../../util/Validations";
import Loader from "@amzn/meridian/loader"
import {getLocalizedMessage} from "../../util/Localization";
import Input from "@amzn/meridian/input";
import editTokens from "@amzn/meridian-tokens/base/icon/edit";
import {postUpdatedRemarkToDevice} from "./network/LEDControllerOverviewNetworkAPI";
import DeleteSingleDeviceModal from "../../commonui/DeleteSingleDeviceModal";
import AddSingleLedControllerModal from "./modal/AddSingleLedControllerModal";
import UpdateDefaultSettingsModal from "./modal/UpdateDefaultSettingsModal";
import {FEATURES, FeatureUtility} from "../../util/FeatureUtility";

const LEDControllerOverview = ({intl}) => {

    const {viewModel, dispatch} = useContext(LEDControllerOverviewContext);
    const [openAddModal, setOpenAddModal] = useState(false)
    const [openActionsMenu, setOpenActionsMenu] = useState({});
    const [openMonitoringModal, setOpenMonitoringModal] = useState(false)
    const [openUpdateDefaultModal, setOpenUpdateDefaultModal] = useState(false)
    const [openDeleteModal, setOpenDeleteModal] = useState(false)
    const actionButtonRefs = useRef([]);
    const [mac, setMac] = useState("");
    const tableEndRef = useRef(null);
    const [remark, setRemark] = useState([]);
    const [editRemark, setEditRemark] = useState(false);
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);

    useEffect(() => {
        if (!openMonitoringModal) {
            getData()
            const interval = setInterval(() => {
                if (!document.hidden) {
                    getData();
                }
            }, getAssetRefreshInterval());
            return () => {
                clearInterval(interval);
            }
        }
    }, [openMonitoringModal]);

    const sortedDeviceList = Object.values(viewModel.ledControllerDeviceInformation);
    useEffect(() => {
        actionButtonRefs.current = actionButtonRefs.current.slice(0, viewModel.ledControllerDeviceInformation.length);
        if(!editRemark) {
            setRemark(sortedDeviceList.map((value) => {
                return {value: value.remarks, close: true}
            }))
        }
    }, [sortedDeviceList]);

    useEffect(() => {
        const disabled = !chainWalk(() => hasAllNonNullFields(viewModel.selectedDefaultValues), false);
        setIsButtonDisabled(disabled);
    }, [viewModel.selectedDefaultValues]);

    const addData = (stateValues, mac) => {
        dispatch({type: 'ADD_DEVICE_DATA', data: {dispatcher: dispatch, stateValues: stateValues, mac: mac}})
    }

    const setMonitoringStatusLoader = (mac) => {
        dispatch({type: 'SET_MONITORING_STATUS_LOADER', data: {mac: mac, monitoringStatusLoading: true}})
    }

    const onClickActionsButton = useCallback((index) => {
        let _open = {...openActionsMenu};
        _open[index] = true;
        setOpenActionsMenu(_open);
    }, []);

    const onCloseActions = useCallback((index) => {
        let _open = {...openActionsMenu};
        _open[index] = false;
        setOpenActionsMenu(_open)
    }, []);

    const onClickDisableMonitoring = (mac) => {
        setMac(mac)
        setOpenMonitoringModal(true)
    }

    const onClickDeleteDevice = (mac) => {
        setMac(mac)
        setOpenDeleteModal(true)
    }

    const onClickEnableMonitoring = (startTime, reason, mac) => {
        const stateValues = {
            "startTime": startTime,
            "endTime": getCurrentDate().toString(),
            "reason": reason
        }
        setMonitoringStatusLoader(mac)
        addData(stateValues, mac);
        getData();
    }

    const onSort = (sortDetails) => {
        setEditRemark(false);
        dispatch({
            type: 'SORT_DATA',
            data: {column: sortDetails.sortColumn, direction: sortDetails.sortDirection}
        })
    }
    sortedDeviceList && sortedDeviceList.sort((a, b) => {
        if (a[viewModel.sortPref.column] < b[viewModel.sortPref.column]) {
            return viewModel.sortPref.direction === "ascending" ? -1 : 1
        }
        if (a[viewModel.sortPref.column] > b[viewModel.sortPref.column]) {
            return viewModel.sortPref.direction === "ascending" ? 1 : -1
        }
        return 0
    })

    const scrollToBottom = () => {
        tableEndRef.current.scrollIntoView({behavior: "smooth"})
    }

    const onClickAddDevice = () => {
        setOpenAddModal(true);
    }

    const onClickUpdateDefaults = () => {
        setOpenUpdateDefaultModal(true);
    }

    const getData = () => {
        setOpenMonitoringModal(false)
        setOpenDeleteModal(false)
        dispatch({type: 'LOAD_VIEW', data: {dispatcher: dispatch}});
    }

    const handleInputChange = (index, value) => {
        const temp = [...remark];
        temp[index] = {...temp[index], value: value};
        setRemark(temp)
    }

    const handleKeyDown = (index, row, event) => {
        if (event.key === "Enter") {
            onClickConfirmRemark(index, row);
        }
    }
    const onClickConfirmRemark = async (index, row) => {
        // function to send the updated remark to post request in db
        const temp = [...remark];
        temp[index] = {...temp[index], close: true};
        setRemark(temp);
        await postUpdatedRemarkToDevice(row, remark[index].value, dispatch);
    }
    const onClickEditRemark = (index) => {
        setEditRemark(true);
        const temp = [...remark];
        temp[index] = {...temp[index], close: false};
        setRemark(temp)
    }

    const renderRemark = (device, index) => {
        return (
            <Row>
                {(remark[index] && remark[index].close === true) && (remark[index].value === "" ?
                    <Text truncate="true"> {device.remark} </Text> :
                    <Text truncate="true"
                          value={remark[index].value}>{remark[index].value} </Text>)}
                {remark[index] && (remark[index].close === false) ?
                    (<Row>
                        <Input
                            onChange={(value) => handleInputChange(index, value)}
                            value={remark[index].value}
                            placeholder="Enter remark"
                            size="small"
                            autoFill="true"
                            focus="true"
                            resize="vertical"
                            onKeyDown={(event) => handleKeyDown(index, device, event)}
                            rows={1}/>
                        <Button
                            onClick={() => onClickConfirmRemark(index, device)}
                            size="small">
                            <FormattedMessage id="modal.confirm" defaultMessage="Confirm"/>
                        </Button>
                    </Row>) :
                    <Button type="link"
                            onClick={() => onClickEditRemark(index)}
                            size="small">
                        <Icon tokens={editTokens}/>
                    </Button>}
            </Row>
        )
    }

    if (viewModel.loadingOverview) {
        return (<LoaderView/>)
    } else {
        return (
            <Row widths="100%"
                 alignmentVertical="top"
                 spacingInset="none none large none">
                <Column>
                    <Row width="100%" widths={["100", "fill", "100"]}>
                        <RefreshTimer deps={[viewModel]}/>
                        <Box></Box>
                        <Tooltip position="top" title={intl.formatMessage({
                            id: 'tooltip.scrolltobottom',
                            defaultMessage: 'Scroll to bottom!'
                        })}>
                            <Button onClick={scrollToBottom}
                                    type="secondary"
                                    size="small">
                                <Icon tokens={chevronDownLargeTokens}/>
                            </Button>
                        </Tooltip>
                    </Row>
                    {sortedDeviceList.length === 0 ?
                        <Alert
                            type="informational"
                            size="small"
                        >
                            <FormattedMessage id="alert.device"
                                              defaultMessage="No device present for this station."/>
                        </Alert> :
                        <Table
                            headerRows={1}
                            spacing="small"
                            sortColumn={viewModel.sortPref.column}
                            sortDirection={viewModel.sortPref.direction}
                            onSort={onSort}
                            showDividers={true}
                            showStripes={true}>
                            <TableRow highlightOnHover={false}>
                                <TableCell sortColumn="mac">
                                    <FormattedMessage id="device.mac_ip" defaultMessage="MAC/IP"/>
                                </TableCell>
                                <TableCell sortColumn="connectivity">
                                    <FormattedMessage id="device.connectivity" defaultMessage="Connection Status"/>
                                </TableCell>
                                <TableCell sortColumn="location">
                                    <FormattedMessage id="device.location" defaultMessage="Location"/>
                                </TableCell>
                                <TableCell>
                                    <FormattedMessage id="device.monitoring" defaultMessage="Monitoring Status"/>
                                </TableCell>
                                <TableCell sortColumn="remarks">
                                    <FormattedMessage id="device.remarks" defaultMessage="Remarks"/>
                                </TableCell>
                                <TableCell>
                                    <FormattedMessage id="device.action" defaultMessage="Action"/>
                                </TableCell>
                            </TableRow>
                            {/*Commenting below line to remove the banner for now. If required, un-comment the below line to add the banner*/}
                            {/*<LEDControllerBanner/>*/}
                            {sortedDeviceList.map((device, index) => {
                                const monitoring = checkDeviceMonitoringEnabled(Number(device.startTime), Number(device.endTime));
                                const {monitoringStatusColor, monitoringStatusId, monitoringStatusDefaultMessage} = getMonitoringStatusDetails(monitoring);
                                return (<TableRow key={device.mac}
                                          highlightOnHover={true}>
                                    <TableCell> <b>{device.mac}</b>
                                        <br></br>
                                        {device.ip}
                                    </TableCell>
                                    <TableCell> <b
                                        style={{color: device.connectivity.toLowerCase() === ONLINE_OK ? BLUE_COLOR : ORANGE_COLOR}}>{device.connectivity}</b>
                                        <br></br>
                                        {device.timestamp}
                                    </TableCell>
                                    <TableCell>
                                        <b>{device.location}</b>
                                    </TableCell>
                                    <TableCell>
                                        {(viewModel.monitoringStatusLoadingDevices[device.mac]) ?
                                            <Tooltip position="start" title={getLocalizedMessage({intl, stringId: 'tooltip.monitoringStatusLoading'})}><Loader size={"medium"}/></Tooltip> :
                                            <b style={{color: monitoringStatusColor}}><FormattedMessage
                                                id={monitoringStatusId}
                                                defaultMessage={monitoringStatusDefaultMessage}/></b>}
                                    </TableCell>
                                    <TableCell width="400px">
                                        {renderRemark(device, index)}
                                    </TableCell>
                                    <TableCell>
                                        <Text type="b300">
                                            <Button ref={el => actionButtonRefs.current[index] = el}
                                                    type="icon"
                                                    size="medium"
                                                    onClick={() => onClickActionsButton(index)}
                                                    aria-controls="my-menu"
                                                    aria-haspopup="menu"
                                                    aria-expanded="false">
                                                ...
                                            </Button>
                                            <Menu anchorNode={actionButtonRefs.current[index]}
                                                  id="my-menu"
                                                  open={openActionsMenu[index] ? openActionsMenu[index] : false}
                                                  position="left"
                                                  onClose={() => onCloseActions(index)}>
                                                {(monitoring.isMonitoringEnabled) ?
                                                    <MenuItem onClick={() => onClickDisableMonitoring(device.mac)}>
                                                        <FormattedMessage id="actions.monitoring_disable"
                                                                          defaultMessage="Disable Monitoring"/>
                                                    </MenuItem> : <MenuItem
                                                        onClick={() => onClickEnableMonitoring(device.startTime, device.reason, device.mac)}>
                                                        <FormattedMessage id="actions.monitoring_enable"
                                                                          defaultMessage="Enable Monitoring"/>
                                                    </MenuItem>}
                                                {FeatureUtility.getFeatureStatus(FEATURES.IS_LED_CONTROLLER_ENHANCEMENT_ENABLED) &&
                                                    <MenuItem onClick={() => onClickDeleteDevice(device.mac)}>
                                                    <FormattedMessage id="actions.delete"
                                                                      defaultMessage="Delete device"/>
                                                </MenuItem>}
                                            </Menu>
                                            {openMonitoringModal && <DisableMonitoringModal mac={mac} state={openMonitoringModal}
                                                                                            onStateChange={setOpenMonitoringModal}
                                                                                            context={LEDControllerOverviewContext}
                                                                                            reducer={'ADD_DEVICE_DATA'}/>}
                                            {openDeleteModal && <DeleteSingleDeviceModal open={openDeleteModal} setOpen={setOpenDeleteModal}
                                                                                         selected={mac} setSelected={setMac} assetType={LED_CONTROLLER_ASSET_TYPE}
                                                                                         macAddressArray={viewModel.macAddressArray} dispatch={dispatch}/>

                                            }
                                        </Text>
                                    </TableCell>
                                </TableRow>)
                            })}
                        </Table>}
                    <div ref={tableEndRef}/>
                    {FeatureUtility.getFeatureStatus(FEATURES.IS_LED_CONTROLLER_ENHANCEMENT_ENABLED) &&
                        <Row width="50%" widths={["auto", "auto", "auto"]}>
                        <Button onClick={onClickAddDevice} type="primary" size="small" disabled={isButtonDisabled}>
                            <Icon tokens={plusCircleTokens}/>
                            <FormattedMessage id="device.add_only_device_button_modal" defaultMessage="Add Single Device Configuration"/>
                        </Button>
                        <Button onClick={onClickUpdateDefaults} type="primary" size="small">
                            <Icon tokens={updateTokens}/>
                            <FormattedMessage id="node.update_defaults_button_modal" defaultMessage="Update Default Settings"/>
                        </Button>
                    </Row>}
                    {
                        openAddModal && <AddSingleLedControllerModal open={openAddModal} setOpen={setOpenAddModal}
                                                                     context={LEDControllerOverviewContext}/>
                    }
                    {
                        openUpdateDefaultModal && <UpdateDefaultSettingsModal open={openUpdateDefaultModal} setOpen={setOpenUpdateDefaultModal}
                                                                                                            context={LEDControllerOverviewContext}/>
                    }
                </Column>
            </Row>
        );
    }
}

export default injectIntl(LEDControllerOverview);


// WEBPACK FOOTER //
// ./src/components/devicemanagement/ledcontroller/LEDControllerOverview.js